import React from "react";
import { motion } from "framer-motion";
import {
  CheckCircle,
  Users,
  Award,
  Clock,
  Leaf,
  DollarSign,
  ThumbsUp,
} from "lucide-react";
import about from "../assets/image/about.jpg";

const About = () => {
  const stats = [
    { label: "Years Experience", value: "10+", icon: Clock },
    { label: "Happy Clients", value: "500+", icon: ThumbsUp },
    { label: "Team Members", value: "100+", icon: Users },
    { label: "Cities Served", value: "15+", icon: Award },
  ];

  const values = [
    {
      icon: Leaf,
      title: "Eco-Friendly",
      description:
        "We only utilize eco-friendly products ensuring spaces are fresh and free from harmful chemicals.",
    },
    {
      icon: Users,
      title: "Professional Team",
      description:
        "Our trained professionals use the finest cleaning equipment to deliver exceptional results.",
    },
    {
      icon: DollarSign,
      title: "Cost-Effective",
      description:
        "High-quality cleaning services at highly affordable rates without compromising on quality.",
    },
    {
      icon: Award,
      title: "Reliable Service",
      description:
        "Prompt and reliable service delivery with consistent cleaning schedules you can count on.",
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-secondary/90" />
        <div className="relative py-24 px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-7xl mx-auto text-center"
          >
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Your Trusted Commercial Cleaning Company
            </h1>
            <p className="mt-6 text-xl text-white/90 max-w-3xl mx-auto">
              Leading the way in professional cleaning services across Australia
              since 2014
            </p>
          </motion.div>
        </div>
      </div>

      {/* Company Introduction */}
      <div className="relative py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                About Envipro Solutions
              </h2>
              <p className="mt-6 text-lg text-gray-600">
                We provide prompt, reliable, and cost-effective cleaning
                services to keep your workplace spotless. There's nothing better
                than working in an environment that is clean and tidy. After
                all, people are inspired to work better when their surroundings
                are immaculate and organized.
              </p>
              <p className="mt-4 text-lg text-gray-600">
                We cater for domestic, commercial, and industrial cleaning
                services and ensure that your property is clean from top to
                bottom, inside and out.
              </p>

              {/* Stats with enhanced design */}
              <div className="mt-12 grid grid-cols-2 gap-6">
                {stats.map((stat) => {
                  const IconComponent = stat.icon;
                  return (
                    <div key={stat.label} className="group relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl transition-all duration-300 group-hover:from-primary/10 group-hover:to-secondary/10" />
                      <div className="relative p-6 rounded-xl">
                        <IconComponent className="h-8 w-8 text-secondary mb-3" />
                        <p className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                          {stat.value}
                        </p>
                        <p className="text-gray-600">{stat.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl transform rotate-3" />
              <img
                className="relative rounded-2xl shadow-xl"
                src={about}
                alt="Our Team"
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Our Values */}
      <div className="relative py-20 overflow-hidden">
        <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-gradient-to-br from-primary/5 to-transparent" />
        <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-to-tl from-secondary/5 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Our Values
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              We built our reputation on strong principles and an unwavering
              commitment to delivering exceptional service.
            </p>
          </motion.div>

          <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative"
                >
                  <div className="relative p-[1px] rounded-xl bg-gradient-to-br from-primary/20 via-secondary/20 to-transparent">
                    <div className="bg-white p-6 rounded-xl relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white group-hover:from-primary/5 group-hover:to-secondary/5 transition-all duration-300" />
                      <div className="relative">
                        <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-gradient-to-r from-primary/10 to-secondary/10 group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300">
                          <IconComponent className="h-6 w-6 text-secondary group-hover:text-primary transition-colors duration-300" />
                        </div>
                        <h3 className="mt-4 text-xl font-semibold text-gray-900 text-center group-hover:text-primary transition-colors duration-300">
                          {value.title}
                        </h3>
                        <p className="mt-2 text-gray-600 text-center">
                          {value.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
